export class GridPaginationManager {
    constructor(gridId = 'paginated-grid', itemsPerPage = 6) {
        this.gridId = gridId;
        this.itemsPerPage = itemsPerPage;
        this.currentPage = 1;
        this.gridItems = [];
        this.filteredItems = [];
        this.totalPages = 1;
        this.activeFilters = {
            category: '',
            tag: ''
        };
    }

    init() {
        const grid = document.getElementById(this.gridId);
        if (!grid) return;

        this.gridItems = Array.from(grid.querySelectorAll('.grid-item'));
        this.filteredItems = [...this.gridItems];
        this.totalPages = Math.ceil(this.filteredItems.length / this.itemsPerPage);

        // Initialize filters from URL parameters
        this.initializeFromQueryString();
        
        // Bind filter events
        this.bindFilterEvents();
        
        // Initial render
        this.applyFilters();
        this.bindPaginationEvents();
    }

    initializeFromQueryString() {
        const urlParams = new URLSearchParams(window.location.search);
        const category = urlParams.get('category');
        const tag = urlParams.get('tag');

        if (category) {
            this.activeFilters.category = category.toLowerCase();
            const categorySelect = document.getElementById('categoryFilter');
            if (categorySelect) {
                categorySelect.value = category.toLowerCase();
            }
        }

        if (tag) {
            this.activeFilters.tag = tag.toLowerCase();
            const tagSelect = document.getElementById('tagFilter');
            if (tagSelect) {
                tagSelect.value = tag.toLowerCase();
            }
        }
    }

    bindFilterEvents() {
        const categoryFilter = document.getElementById('categoryFilter');
        const tagFilter = document.getElementById('tagFilter');
        const clearButton = document.getElementById('clearFilters');

        if (categoryFilter) {
            categoryFilter.addEventListener('change', (e) => {
                this.activeFilters.category = e.target.value;
                this.applyFilters();
                this.updateQueryString();
            });
        }

        if (tagFilter) {
            tagFilter.addEventListener('change', (e) => {
                this.activeFilters.tag = e.target.value;
                this.applyFilters();
                this.updateQueryString();
            });
        }

        if (clearButton) {
            clearButton.addEventListener('click', () => {
                this.clearFilters();
            });
        }
    }

    clearFilters() {
        this.activeFilters.category = '';
        this.activeFilters.tag = '';
        
        const categoryFilter = document.getElementById('categoryFilter');
        const tagFilter = document.getElementById('tagFilter');
        
        if (categoryFilter) categoryFilter.value = '';
        if (tagFilter) tagFilter.value = '';
        
        this.applyFilters();
        this.updateQueryString();
    }

    applyFilters() {
        // Filter items based on active filters
        this.filteredItems = this.gridItems.filter(item => {
            let categoryMatch = true;
            let tagMatch = true;

            // Check category filter
            if (this.activeFilters.category) {
                const itemCategories = (item.dataset.category || '').toLowerCase().split(',');
                categoryMatch = itemCategories.includes(this.activeFilters.category);
            }

            // Check tag filter
            if (this.activeFilters.tag) {
                const itemTags = (item.dataset.tags || '').toLowerCase().split(',');
                tagMatch = itemTags.includes(this.activeFilters.tag);
            }

            return categoryMatch && tagMatch;
        });

        // Reset to first page when filters change
        this.currentPage = 1;
        this.totalPages = Math.ceil(this.filteredItems.length / this.itemsPerPage);
        
        // Render the filtered results
        this.render();
    }

    updateQueryString() {
        const params = new URLSearchParams();
        
        if (this.activeFilters.category) {
            params.set('category', this.activeFilters.category);
        }
        
        if (this.activeFilters.tag) {
            params.set('tag', this.activeFilters.tag);
        }
        
        const newUrl = params.toString() 
            ? `${window.location.pathname}?${params.toString()}` 
            : window.location.pathname;
        
        window.history.pushState({}, '', newUrl);
    }

    getCurrentPageItems() {
        const start = (this.currentPage - 1) * this.itemsPerPage;
        return this.filteredItems.slice(start, start + this.itemsPerPage);
    }

    render() {
        const grid = document.getElementById(this.gridId);
        if (!grid) return;

        grid.innerHTML = '';
        
        if (this.filteredItems.length === 0) {
            // Show no results message
            grid.innerHTML = `
                <div class="col-12 text-center py-5">
                    <p class="text-muted">No articles found matching your filters.</p>
                    <button class="btn btn-primary mt-3" onclick="window.gridPaginationManager.clearFilters()">
                        Clear Filters
                    </button>
                </div>
            `;
        } else {
            // Show filtered items for current page
            this.getCurrentPageItems().forEach(item => grid.appendChild(item));
        }
        
        this.renderPagination();
    }

    renderPagination() {
        const container = document.querySelector('.pagination');
        if (!container) return;

        // Hide pagination if no items or only one page
        if (this.filteredItems.length === 0 || this.totalPages <= 1) {
            container.innerHTML = '';
            return;
        }

        let html = `
            <li class="page-item ${this.currentPage === 1 ? 'disabled' : ''}">
                <a class="page-link" href="#" aria-label="Previous page">Previous</a>
            </li>
        `;

        // Smart pagination with ellipsis for many pages
        const maxVisible = 5;
        let startPage = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(this.totalPages, startPage + maxVisible - 1);
        
        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            html += `
                <li class="page-item">
                    <a class="page-link" href="#" aria-label="Page 1">1</a>
                </li>
            `;
            if (startPage > 2) {
                html += `<li class="page-item disabled"><span class="page-link">...</span></li>`;
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            html += `
                <li class="page-item ${i === this.currentPage ? 'active' : ''}">
                    <a class="page-link" href="#" aria-label="Page ${i}" ${i === this.currentPage ? 'aria-current="page"' : ''}>${i}</a>
                </li>
            `;
        }

        if (endPage < this.totalPages) {
            if (endPage < this.totalPages - 1) {
                html += `<li class="page-item disabled"><span class="page-link">...</span></li>`;
            }
            html += `
                <li class="page-item">
                    <a class="page-link" href="#" aria-label="Page ${this.totalPages}">${this.totalPages}</a>
                </li>
            `;
        }

        html += `
            <li class="page-item ${this.currentPage === this.totalPages ? 'disabled' : ''}">
                <a class="page-link" href="#" aria-label="Next page">Next</a>
            </li>
        `;

        container.innerHTML = html;
    }

    bindPaginationEvents() {
        const container = document.querySelector('.pagination');
        if (!container) return;

        container.addEventListener('click', (e) => {
            e.preventDefault();
            const link = e.target.closest('.page-link');
            if (!link || link.parentElement.classList.contains('disabled')) return;

            const label = link.getAttribute('aria-label');
            if (label?.startsWith('Previous')) this.previousPage();
            else if (label?.startsWith('Next')) this.nextPage();
            else if (!isNaN(link.textContent.trim())) this.goToPage(parseInt(link.textContent.trim()));
        });
    }

    goToPage(page) {
        if (page >= 1 && page <= this.totalPages) {
            this.currentPage = page;
            this.render();
            // Scroll to top of grid
            const grid = document.getElementById(this.gridId);
            if (grid) {
                grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }

    nextPage() {
        if (this.currentPage < this.totalPages) {
            this.currentPage++;
            this.render();
            // Scroll to top of grid
            const grid = document.getElementById(this.gridId);
            if (grid) {
                grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }

    previousPage() {
        if (this.currentPage > 1) {
            this.currentPage--;
            this.render();
            // Scroll to top of grid
            const grid = document.getElementById(this.gridId);
            if (grid) {
                grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }
}

// Conditional initializer (for bundling)
export function initializeGridPagination({ gridId = 'paginated-grid', itemsPerPage = 6 } = {}) {
    const grid = document.getElementById(gridId);
    if (!grid) return;

    const manager = new GridPaginationManager(gridId, itemsPerPage);
    window.gridPaginationManager = manager;
    manager.init();
    return manager;
}